import { useEffect } from 'react'
import { zoomStep } from '@/components/layout/TopBar'
import { openFromPicker, printDocument, save, saveAs } from '@/lib/commands'
import { translateAnn } from '@/lib/geometry'
import { pickAndPlaceImage } from '@/lib/images'
import { findAnnotation, useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import type { Annotation, Tool } from '@/types'

const TOOL_KEYS: Record<string, Tool> = {
  v: 'select',
  h: 'hand',
  e: 'editText',
  t: 'text',
  u: 'highlight',
  p: 'pen',
  d: 'pen',
  m: 'marker',
  r: 'rect',
  o: 'ellipse',
  l: 'line',
  a: 'arrow',
  n: 'note',
  x: 'eraser',
}

function isTyping(t: EventTarget | null) {
  const el = t as HTMLElement | null
  if (!el) return false
  if (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true
  if (el.tagName !== 'INPUT') return false
  const type = (el as HTMLInputElement).type
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'].includes(type)
}

export function useKeyboardShortcuts() {
  useEffect(() => {
    let spaceTool: Tool | null = null
    const onKey = (e: KeyboardEvent) => {
      const doc = useDoc.getState()
      const ui = useUi.getState()
      const mod = e.ctrlKey || e.metaKey
      const k = e.key.toLowerCase()

      // global shortcuts that work even without a document
      if (mod && k === 'o') {
        e.preventDefault()
        void openFromPicker()
        return
      }
      if (doc.status !== 'ready') return
      if (mod && k === 's') {
        e.preventDefault()
        void (e.shiftKey ? saveAs() : save())
        return
      }
      if (mod && k === 'p') {
        e.preventDefault()
        void printDocument()
        return
      }
      if (mod && k === 'f') {
        e.preventDefault()
        ui.setSearchOpen(true)
        return
      }
      if (mod && (k === '=' || k === '+')) {
        e.preventDefault()
        ui.setZoom(zoomStep(ui.zoom, 1))
        return
      }
      if (mod && k === '-') {
        e.preventDefault()
        ui.setZoom(zoomStep(ui.zoom, -1))
        return
      }
      if (mod && k === '0') {
        e.preventDefault()
        ui.setFit('page')
        return
      }
      if (mod && k === '1') {
        e.preventDefault()
        ui.setZoom(1)
        return
      }
      if (isTyping(e.target)) return

      if (mod && k === 'z') {
        e.preventDefault()
        if (e.shiftKey) doc.redo()
        else doc.undo()
        return
      }
      if (mod && k === 'y') {
        e.preventDefault()
        doc.redo()
        return
      }
      if (mod && k === 'd' && ui.selection.length) {
        e.preventDefault()
        ui.select(doc.duplicateAnnotations(ui.selection))
        return
      }
      if (mod && k === 'a' && ui.tool === 'select') {
        const page = doc.pages[ui.currentPage]
        if (page) {
          e.preventDefault()
          ui.select((doc.annotations[page.id] ?? []).map((a) => a.id))
        }
        return
      }
      if (mod || e.altKey) return

      if ((e.key === 'Delete' || e.key === 'Backspace') && ui.selection.length) {
        e.preventDefault()
        doc.deleteAnnotations(ui.selection)
        ui.select([])
        return
      }
      if (e.key === 'Escape') {
        if (ui.searchOpen) ui.setSearchOpen(false)
        else if (ui.selection.length) ui.select([])
        else if (ui.tool !== 'select') ui.setTool('select')
        window.getSelection()?.removeAllRanges()
        return
      }
      if (e.key.startsWith('Arrow') && ui.selection.length) {
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
        doc.commit((s) => {
          const annotations = { ...s.annotations }
          for (const id of ui.selection) {
            const a = findAnnotation(s, id)
            if (!a) continue
            annotations[a.pageId] = annotations[a.pageId].map((x) =>
              x.id === id ? (translateAnn(x, dx, dy) as Annotation) : x,
            )
          }
          return { ...s, annotations }
        })
        return
      }
      if ((e.key === 'PageDown' || e.key === 'ArrowRight') && ui.fit === 'page') {
        e.preventDefault()
        ui.goToPage(Math.min(doc.pages.length - 1, ui.currentPage + 1))
        return
      }
      if ((e.key === 'PageUp' || e.key === 'ArrowLeft') && ui.fit === 'page') {
        e.preventDefault()
        ui.goToPage(Math.max(0, ui.currentPage - 1))
        return
      }
      if (e.key === 'Home') {
        ui.goToPage(0)
        return
      }
      if (e.key === 'End') {
        ui.goToPage(doc.pages.length - 1)
        return
      }
      if (e.key === '?') {
        ui.openDialog('shortcuts')
        return
      }
      if (e.key === ' ' && !e.repeat && ui.tool !== 'hand') {
        // hold space to pan temporarily
        spaceTool = ui.tool
        ui.setTool('hand')
        e.preventDefault()
        return
      }
      if (k === 's' && !e.shiftKey) {
        ui.openDialog('signature')
        return
      }
      if (k === 'i' && !e.shiftKey) {
        void pickAndPlaceImage()
        return
      }
      const tool = TOOL_KEYS[k]
      if (tool && !e.shiftKey) {
        ui.setTool(tool)
        return
      }
    }
    const onUp = (e: KeyboardEvent) => {
      if (e.key === ' ' && spaceTool) {
        useUi.getState().setTool(spaceTool)
        spaceTool = null
      }
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('keyup', onUp)
    }
  }, [])
}
